import React, { useState, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button as MuiButton,
    Box,
    Typography,
    LinearProgress,
    Alert,
    Chip,
    IconButton,
    Divider,
    Paper,
    Stack,
} from '@mui/material';
import { CloudUpload, Close, Check } from '@mui/icons-material';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { apiClient } from '../../utils/apiClient';

type Step = 'upload' | 'parsing' | 'review' | 'submitting' | 'result';

interface ImportRow {
    brand_name: string;
    model_name: string;
}

interface ClassifiedRow extends ImportRow {
    duplicate: boolean;
}

interface BadRow {
    row_index: number;
    reason: 'missing_brand_name' | 'missing_model_name';
    brand_name?: string;
    model_name?: string;
}

interface RejectedRow {
    row_index: number;
    reason: 'missing_brand_name' | 'missing_model_name' | 'duplicate_in_file';
    brand_name?: string;
    model_name?: string;
}

interface ImportResponse {
    success: boolean;
    mode: string;
    totals: {
        rows_received: number;
        brands_created: number;
        models_inserted: number;
        models_skipped: number;
        models_replaced_count: number;
        rows_rejected_missing_fields?: number;
        rows_rejected_duplicate_in_file?: number;
    };
    failures: Array<{ batch_index: number; batch_size: number; error_message: string }>;
    rejected_rows?: RejectedRow[];
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

interface CsvImportDialogProps {
    open: boolean;
    onClose: () => void;
}

const normalize = (s: string): string =>
    s.trim().toLowerCase().replace(/\s+/g, ' ');

const parseCsv = (text: string): { rows: ImportRow[]; bad: BadRow[] } => {
    const rows: ImportRow[] = [];
    const bad: BadRow[] = [];
    // Strip UTF-8 BOM if present (Excel adds it on save).
    const cleaned = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
    const lines = cleaned.split(/\r?\n/);
    if (lines.length === 0) return { rows, bad };

    const splitCsvLine = (line: string): string[] => {
        const out: string[] = [];
        let cur = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (inQuotes) {
                if (ch === '"' && line[i + 1] === '"') {
                    cur += '"';
                    i++;
                } else if (ch === '"') {
                    inQuotes = false;
                } else {
                    cur += ch;
                }
            } else {
                if (ch === '"') {
                    inQuotes = true;
                } else if (ch === ',') {
                    out.push(cur);
                    cur = '';
                } else {
                    cur += ch;
                }
            }
        }
        out.push(cur);
        return out;
    };

    const header = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
    const brandIdx = header.indexOf('brand_name');
    const modelIdx = header.indexOf('model_name');
    if (brandIdx < 0 || modelIdx < 0) {
        throw new Error('CSV must include "brand_name" and "model_name" columns in the header row');
    }

    for (let i = 1; i < lines.length; i++) {
        const raw = lines[i];
        if (!raw.trim()) continue;
        const cols = splitCsvLine(raw);
        const brand = (cols[brandIdx] || '').trim();
        const model = (cols[modelIdx] || '').trim();
        if (!brand) {
            bad.push({
                row_index: i,
                reason: 'missing_brand_name',
                brand_name: brand || undefined,
                model_name: model || undefined,
            });
            continue;
        }
        if (!model) {
            bad.push({
                row_index: i,
                reason: 'missing_model_name',
                brand_name: brand,
                model_name: model || undefined,
            });
            continue;
        }
        rows.push({ brand_name: brand, model_name: model });
    }
    return { rows, bad };
};

export const CsvImportDialog: React.FC<CsvImportDialogProps> = ({ open, onClose }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();

    const [step, setStep] = useState<Step>('upload');
    const [classified, setClassified] = useState<ClassifiedRow[]>([]);
    const [badRows, setBadRows] = useState<BadRow[]>([]);
    const [result, setResult] = useState<ImportResponse | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const resetAll = useCallback(() => {
        setStep('upload');
        setClassified([]);
        setBadRows([]);
        setResult(null);
        setErrorMsg(null);
    }, []);

    useEffect(() => {
        if (!open) resetAll();
    }, [open, resetAll]);

    const handleFile = async (chosen: File) => {
        const lower = chosen.name.toLowerCase();
        const isCsv = lower.endsWith('.csv') || chosen.type === 'text/csv';
        if (!isCsv) {
            notify('resources.vehicle-models.import.errors.notSupported', {
                type: 'error',
                messageArgs: { _: 'Please select a CSV file' },
            });
            return;
        }
        if (chosen.size > MAX_FILE_SIZE_BYTES) {
            notify('resources.vehicle-models.import.errors.tooLarge', {
                type: 'error',
                messageArgs: { _: 'File exceeds 10MB' },
            });
            return;
        }

        setStep('parsing');
        setErrorMsg(null);

        try {
            const text = await chosen.text();
            const { rows, bad } = parseCsv(text);
            setBadRows(bad);
            if (rows.length === 0 && bad.length === 0) {
                throw new Error('No valid rows found in the file');
            }
            if (rows.length === 0 && bad.length > 0) {
                throw new Error(
                    `All ${bad.length} rows are missing brand_name or model_name. Fix the file and re-upload.`,
                );
            }

            const existingResp = await apiClient.get<any>('/api/backend/vehicle-models?perPage=10000&page=1');
            const existing = ((existingResp as any).data || []) as Array<{ brand_name?: string; name?: string }>;
            const existingSet = new Set(
                existing
                    .filter((m) => m.brand_name && m.name)
                    .map((m) => `${normalize(m.brand_name as string)}::${normalize(m.name as string)}`),
            );

            const seen = new Set<string>();
            const out: ClassifiedRow[] = [];
            for (const r of rows) {
                const key = `${normalize(r.brand_name)}::${normalize(r.model_name)}`;
                if (seen.has(key)) continue;
                seen.add(key);
                out.push({ ...r, duplicate: existingSet.has(key) });
            }

            setClassified(out);
            setStep('review');
        } catch (e: any) {
            setErrorMsg(e?.message || translate('resources.vehicle-models.import.errors.parseFailed', {
                _: "Couldn't parse file",
            }));
            setStep('upload');
        }
    };

    const newRows = classified.filter((r) => !r.duplicate);
    const duplicateRows = classified.filter((r) => r.duplicate);

    const handleSubmit = async () => {
        if (newRows.length === 0) return;
        setStep('submitting');
        setErrorMsg(null);
        try {
            const payloadRows = newRows.map((r) => ({ brand_name: r.brand_name, model_name: r.model_name }));
            const resp = await apiClient.post<ImportResponse>(
                '/api/backend/vehicle-models/import',
                { mode: 'add', rows: payloadRows },
            );
            setResult(resp);
            setStep('result');
            notify('resources.vehicle-models.import.success', {
                type: 'success',
                messageArgs: { _: 'Import complete' },
            });
            refresh();
        } catch (e: any) {
            setErrorMsg(e?.body?.error || e?.message || 'Import failed');
            setStep('review');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">
                        {translate('resources.vehicle-models.import.title', { _: 'Import models from CSV' })}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {errorMsg && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMsg}
                    </Alert>
                )}

                {step === 'upload' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                        <CloudUpload sx={{ fontSize: 64, color: '#00C853', mb: 2 }} />
                        <Typography sx={{ mb: 1, textAlign: 'center' }}>
                            {translate('resources.vehicle-models.import.uploadHint', {
                                _: 'Choose a CSV file (max 10MB) with columns "brand_name" and "model_name".',
                            })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                            {translate('resources.vehicle-models.import.uploadHintLine2', {
                                _: 'Tip: run the parse-epd-ev-pdf skill to convert the EPD HK PDF into this format.',
                            })}
                        </Typography>
                        <MuiButton variant="contained" component="label">
                            {translate('resources.vehicle-models.import.selectFile', { _: 'Select file' })}
                            <input
                                type="file"
                                hidden
                                accept=".csv,text/csv"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleFile(f);
                                }}
                            />
                        </MuiButton>
                    </Box>
                )}

                {step === 'parsing' && (
                    <Box sx={{ py: 4 }}>
                        <Typography sx={{ mb: 2 }}>
                            {translate('resources.vehicle-models.import.parsing', {
                                _: 'Parsing file and checking against existing catalog...',
                            })}
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}

                {step === 'review' && (
                    <Stack spacing={2}>
                        <Alert severity={newRows.length > 0 ? 'info' : 'warning'}>
                            {translate('resources.vehicle-models.import.summary', {
                                _: `${classified.length} rows: ${newRows.length} new, ${duplicateRows.length} already exist (will be skipped).`,
                                total: classified.length,
                                fresh: newRows.length,
                                existing: duplicateRows.length,
                            })}
                        </Alert>

                        {badRows.length > 0 && (
                            <Alert severity="warning">
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {translate('resources.vehicle-models.import.badRowsHeader', {
                                        _: `${badRows.length} row(s) are missing brand_name or model_name and will NOT be imported. Fix the file to include them.`,
                                        count: badRows.length,
                                    })}
                                </Typography>
                                <Box sx={{ maxHeight: 160, overflow: 'auto' }}>
                                    {badRows.slice(0, 50).map((br, i) => (
                                        <Typography key={i} variant="caption" component="div">
                                            Row {br.row_index + 1}: {br.reason}
                                            {br.brand_name ? ` — brand: "${br.brand_name}"` : ''}
                                            {br.model_name ? `, model: "${br.model_name}"` : ''}
                                        </Typography>
                                    ))}
                                    {badRows.length > 50 && (
                                        <Typography variant="caption" color="text.secondary">
                                            ... and {badRows.length - 50} more
                                        </Typography>
                                    )}
                                </Box>
                            </Alert>
                        )}

                        {newRows.length > 0 && (
                            <Box>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    {translate('resources.vehicle-models.import.willInsert', {
                                        _: `Will be inserted (${newRows.length})`,
                                        count: newRows.length,
                                    })}
                                </Typography>
                                <Paper variant="outlined" sx={{ maxHeight: 240, overflow: 'auto', p: 1 }}>
                                    {newRows.slice(0, 100).map((row, i) => (
                                        <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.5, alignItems: 'center' }}>
                                            <Chip size="small" color="success" label={row.brand_name} />
                                            <Typography variant="body2">{row.model_name}</Typography>
                                        </Box>
                                    ))}
                                    {newRows.length > 100 && (
                                        <Typography variant="caption" color="text.secondary">
                                            ... and {newRows.length - 100} more
                                        </Typography>
                                    )}
                                </Paper>
                            </Box>
                        )}

                        {duplicateRows.length > 0 && (
                            <Box>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    {translate('resources.vehicle-models.import.willSkip', {
                                        _: `Already in catalog — will be skipped (${duplicateRows.length})`,
                                        count: duplicateRows.length,
                                    })}
                                </Typography>
                                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1, opacity: 0.7 }}>
                                    {duplicateRows.slice(0, 50).map((row, i) => (
                                        <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.5, alignItems: 'center' }}>
                                            <Chip size="small" color="default" label={row.brand_name} />
                                            <Typography variant="body2">{row.model_name}</Typography>
                                        </Box>
                                    ))}
                                    {duplicateRows.length > 50 && (
                                        <Typography variant="caption" color="text.secondary">
                                            ... and {duplicateRows.length - 50} more
                                        </Typography>
                                    )}
                                </Paper>
                            </Box>
                        )}

                        <Divider />
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.vehicle-models.import.addOnlyNote', {
                                _: 'Existing brands/models are never deleted — only new entries are added.',
                            })}
                        </Typography>
                    </Stack>
                )}

                {step === 'submitting' && (
                    <Box sx={{ py: 4 }}>
                        <Typography sx={{ mb: 2 }}>
                            {translate('resources.vehicle-models.import.submitting', { _: 'Submitting...' })}
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}

                {step === 'result' && result && (
                    <Stack spacing={2}>
                        <Alert severity={result.failures.length === 0 ? 'success' : 'warning'} icon={<Check />}>
                            {translate('resources.vehicle-models.import.completeMsg', {
                                _: 'Import complete.',
                            })}
                        </Alert>
                        <Box>
                            <Typography variant="body2">
                                {translate('resources.vehicle-models.import.rowsReceived', { _: 'Rows received' })}: {result.totals.rows_received}
                            </Typography>
                            <Typography variant="body2">
                                {translate('resources.vehicle-models.import.brandsCreated', { _: 'Brands created' })}: {result.totals.brands_created}
                            </Typography>
                            <Typography variant="body2">
                                {translate('resources.vehicle-models.import.modelsInserted', { _: 'Models inserted' })}: {result.totals.models_inserted}
                            </Typography>
                            <Typography variant="body2">
                                {translate('resources.vehicle-models.import.modelsSkipped', { _: 'Models skipped (already existed)' })}: {result.totals.models_skipped}
                            </Typography>
                        </Box>
                        {result.failures.length > 0 && (
                            <Alert severity="warning">
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {result.failures.length} batch(es) failed:
                                </Typography>
                                {result.failures.map((f, i) => (
                                    <Typography key={i} variant="caption" component="div">
                                        Batch {f.batch_index} ({f.batch_size} rows): {f.error_message}
                                    </Typography>
                                ))}
                            </Alert>
                        )}
                        {result.rejected_rows && result.rejected_rows.length > 0 && (
                            <Alert severity="warning">
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {translate('resources.vehicle-models.import.rejectedHeader', {
                                        _: `${result.rejected_rows.length} rows rejected — please fix and re-import:`,
                                        count: result.rejected_rows.length,
                                    })}
                                </Typography>
                                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                    {result.rejected_rows.slice(0, 100).map((rr, i) => (
                                        <Typography key={i} variant="caption" component="div">
                                            Row {rr.row_index + 2}: {rr.reason}
                                            {rr.brand_name ? ` — brand: "${rr.brand_name}"` : ''}
                                            {rr.model_name ? `, model: "${rr.model_name}"` : ''}
                                        </Typography>
                                    ))}
                                    {result.rejected_rows.length > 100 && (
                                        <Typography variant="caption" color="text.secondary">
                                            ... and {result.rejected_rows.length - 100} more
                                        </Typography>
                                    )}
                                </Box>
                            </Alert>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {step === 'review' && (
                    <>
                        <MuiButton onClick={onClose}>
                            {translate('ra.action.cancel', { _: 'Cancel' })}
                        </MuiButton>
                        <MuiButton
                            variant="contained"
                            disabled={newRows.length === 0}
                            onClick={handleSubmit}
                        >
                            {translate('resources.vehicle-models.import.submit', {
                                _: `Import ${newRows.length} new`,
                                count: newRows.length,
                            })}
                        </MuiButton>
                    </>
                )}
                {step === 'result' && (
                    <MuiButton variant="contained" onClick={onClose}>
                        {translate('ra.action.close', { _: 'Close' })}
                    </MuiButton>
                )}
                {step === 'upload' && (
                    <MuiButton onClick={onClose}>
                        {translate('ra.action.cancel', { _: 'Cancel' })}
                    </MuiButton>
                )}
            </DialogActions>
        </Dialog>
    );
};
